import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import api from '../utils/api';
import PhoneInput from '../components/PhoneInput';
import CustomerSignInModal from '../components/CustomerSignInModal';
import { getMarketplaceCountry } from '../utils/marketplaceLocale';

// ── Ticket icon ───────────────────────────────────────────────────────────────
const TicketIcon = () => (
  <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5"
      d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export default function FindBookingPage() {
  // Steps: 'lookup' → 'choose' → 'send' → 'done'
  const [step, setStep] = useState('lookup');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState(''); // E.164 from PhoneInput
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [sent, setSent] = useState(null); // { masked, delivery }
  const [showSignIn, setShowSignIn] = useState(false);

  // Pagination for booking list
  const PAGE_SIZE = 8;
  const [page, setPage] = useState(0);
  const handleLookup = async (e) => {
    e.preventDefault();
    setError('');
    if (!lastName.trim()) { setError('Please enter your last name'); return; }
    if (phone.replace(/\D/g, '').length < 8) { setError('Please enter a valid phone number'); return; }

    setLoading(true);
    try {
      const res = await api.post('/v2/public/find-booking', {
        lastName: lastName.trim(),
        phone,
      });
      const found = res.data.bookings || [];
      setBookings(found);
      if (found.length === 1) {
        setSelectedBooking(found[0]);
        setStep('send');
      } else {
        setStep('choose');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'No booking found with those details. Please check your last name and phone number.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 3: Send ticket ──────────────────────────────────────────────────
  const handleSend = async (delivery) => {
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/v2/public/find-booking/send', {
        confirmationCode: selectedBooking.confirmationCode,
        delivery,
      });
      setSent({ masked: res.data.masked, delivery: res.data.delivery || delivery });
      setStep('done');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to send. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isWhatsApp = selectedBooking?.smsChannel === 'whatsapp';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-gray-50 flex flex-col items-center justify-center p-4">
      {/* Back link — minimal, no header bar */}
      <div className="w-full max-w-md mb-6">
        <Link to="/" className="text-sm text-gray-400 hover:text-gray-600 transition-colors flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </Link>
      </div>

      <Card className="max-w-md w-full shadow-xl border-0">
        <CardContent className="p-8">

          {/* ── STEP 1: Lookup ── */}
          {step === 'lookup' && (
            <>
              <div className="text-center mb-7">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-5">
                  <TicketIcon />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Find My Booking</h1>
                <p className="text-gray-500 text-sm">
                  Enter your last name and the phone number you used when booking.
                  We'll send your ticket to your registered contact.
                </p>
              </div>

              <form onSubmit={handleLookup} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                  <Input
                    placeholder="Your last name"
                    value={lastName}
                    onChange={e => setLastName(e.target.value.replace(/[^a-zA-Z\s'\-.]/g, '').slice(0, 50))}
                    maxLength={50}
                    className="h-12 text-base border-gray-200 focus:border-blue-500"
                    autoFocus
                    autoComplete="family-name"
                    data-testid="find-booking-lastname"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                  <PhoneInput
                    value={phone}
                    defaultCountry={getMarketplaceCountry() === 'UAE' ? 'AE' : 'US'}
                    onChange={(e164) => setPhone(e164)}
                    data-testid="find-booking-phone"
                  />
                  <p className="text-xs text-gray-400 mt-1">Pick your country, then the number you booked with</p>
                </div>

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700" data-testid="find-booking-error">{error}</p>
                  </div>
                )}

                <Button type="submit"
                  disabled={loading || !lastName.trim() || phone.replace(/\D/g, '').length < 8}
                  className="w-full h-12 text-base font-semibold bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="find-booking-submit">
                  {loading ? 'Searching...' : 'Find My Booking'}
                </Button>
                <p className="text-xs text-gray-400 text-center">5 attempts max · your details are never stored</p>
              </form>

              {/* Third recovery option — BookingFlows profile */}
              <div className="mt-6 pt-5 border-t border-gray-100 text-center">
                <button
                  onClick={() => setShowSignIn(true)}
                  className="text-sm font-medium text-blue-600 hover:text-blue-700"
                  data-testid="find-booking-profile-signin"
                >
                  Or log into your BookingFlows profile →
                </button>
                <p className="text-xs text-gray-400 mt-1">All your trips and tickets, one account.</p>
              </div>
            </>
          )}

          {/* ── STEP 2: Choose booking (multiple found) ── */}
          {step === 'choose' && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-1">We found {bookings.length} bookings</h2>
                <p className="text-gray-500 text-sm">Select the booking you'd like to access</p>
              </div>

              <div className="space-y-3">
                {bookings.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((b) => (
                  <button
                    key={b.confirmationCode}
                    onClick={() => { setSelectedBooking(b); setStep('send'); }}
                    className="w-full text-left p-4 rounded-xl border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{b.serviceName}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{b.date}{b.orgName ? ` · ${b.orgName}` : ''}</p>
                      </div>
                      <span className="text-xs font-mono text-gray-400">{b.confirmationCode}</span>
                    </div>
                  </button>
                ))}
              </div>

              {/* Pagination */}
              {bookings.length > PAGE_SIZE && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="text-sm text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/></svg>
                    Previous
                  </button>
                  <span className="text-xs text-gray-400">
                    {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, bookings.length)} of {bookings.length}
                  </span>
                  <button
                    onClick={() => setPage(p => p + 1)}
                    disabled={(page + 1) * PAGE_SIZE >= bookings.length}
                    className="text-sm text-gray-500 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    Next
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/></svg>
                  </button>
                </div>
              )}

              <button onClick={() => { setStep('lookup'); setError(''); setPage(0); }}
                className="w-full text-center text-sm text-gray-400 hover:text-gray-600 mt-4">
                ← Search again
              </button>
            </>
          )}

          {/* ── STEP 3: Choose delivery method ── */}
          {step === 'send' && selectedBooking && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-1">Booking found!</h2>
                <div className="inline-flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-1.5 mt-1">
                  <span className="text-sm text-gray-600">{selectedBooking.serviceName}</span>
                  <span className="text-gray-300">·</span>
                  <span className="text-xs font-mono text-gray-500">{selectedBooking.confirmationCode}</span>
                </div>
              </div>

              <p className="text-sm text-gray-600 text-center mb-5 font-medium">How should we send your ticket?</p>

              <div className="space-y-3">
                {selectedBooking.hasEmail && (
                  <button
                    onClick={() => handleSend('email')}
                    disabled={loading}
                    className="w-full flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all disabled:opacity-50"
                    data-testid="send-ticket-email"
                  >
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-semibold text-gray-900">Email my ticket</p>
                      <p className="text-xs text-gray-500">Send to {selectedBooking.maskedEmail}</p>
                    </div>
                  </button>
                )}

                {selectedBooking.hasPhone && (
                  <button
                    onClick={() => handleSend('sms')}
                    disabled={loading}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border border-gray-200 transition-all disabled:opacity-50 ${isWhatsApp ? 'hover:border-green-400 hover:bg-green-50' : 'hover:border-blue-400 hover:bg-blue-50'}`}
                    data-testid="send-ticket-sms"
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isWhatsApp ? 'bg-green-100' : 'bg-blue-100'}`}>
                      {isWhatsApp ? <WhatsAppIcon /> : (
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      )}
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-semibold text-gray-900">{isWhatsApp ? 'WhatsApp my ticket' : 'Text my ticket'}</p>
                      <p className="text-xs text-gray-500">Send to number {selectedBooking.maskedPhone}</p>
                    </div>
                  </button>
                )}
              </div>

              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {loading && <p className="text-center text-sm text-gray-400 mt-4">Sending...</p>}

              {bookings.length > 1 && (
                <button onClick={() => setStep('choose')}
                  className="w-full text-center text-sm text-gray-400 hover:text-gray-600 mt-4">
                  ← Choose a different booking
                </button>
              )}
            </>
          )}

          {/* ── STEP 4: Sent ── */}
          {step === 'done' && sent && (
            <div className="text-center py-4">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {sent.delivery === 'email' ? 'Email sent!' : sent.delivery === 'whatsapp' ? 'WhatsApp sent!' : 'Text sent!'}
              </h2>
              <p className="text-gray-500 text-sm mb-1">
                Your ticket link was sent {sent.delivery === 'email' ? 'by email' : sent.delivery === 'whatsapp' ? 'on WhatsApp' : 'by text'} to
              </p>
              <p className="font-mono text-sm font-semibold text-gray-800 mb-6">{sent.masked}</p>
              <p className="text-xs text-gray-400">
                Check your {sent.delivery === 'email' ? 'inbox (and spam folder)' : sent.delivery === 'whatsapp' ? 'WhatsApp messages' : 'messages'}.
                The link is secure and expires in 30 days.
              </p>
              <button onClick={() => { setStep('lookup'); setLastName(''); setPhone(''); setError(''); setSent(null); }}
                className="mt-6 text-sm text-blue-600 hover:underline">
                Look up another booking
              </button>
            </div>
          )}

        </CardContent>
      </Card>

      <CustomerSignInModal
        open={showSignIn}
        onOpenChange={setShowSignIn}
        onSuccess={() => { window.location.href = '/my-trips'; }}
      />
    </div>
  );
}
